import { useOutletContext } from "react-router";

import type { OrgRecord } from "@/shared/domain/orgs";

/**
 * Outlet-context payload that the `/devices/:deviceId` parent layout
 * (`<Device />`) hands to each child tab (sessions, datasets, configurations,
 * files, metrics, details, tokens) through `<Outlet context>`.
 *
 * Fields:
 * 1. `deviceId` — resolved from the URL path param; non-empty by the time
 *    a tab renders, since the parent layout short-circuits to an error
 *    state when it is missing.
 * 2. `org` — the viewer's active organization, or `null` while auth is
 *    still resolving.
 *
 * Carries no device record: the tabs that need it (details, configurations)
 * fetch it via `useDevice`, which TanStack Query caches, so siblings that need
 * only the id make no extra request.
 */
export type DeviceContext = {
  deviceId: string;
  org: OrgRecord | null;
};

/**
 * Read `{ deviceId, org }` from the device-detail outlet context.
 *
 * Call from any descendant route of `<Device />` (i.e. any tab under
 * `/devices/:deviceId`) to get the same bundle the parent layout
 * already resolved, rather than re-reading `useParams` / `useAuth`
 * separately in every tab.
 *
 * Throws if called outside a `<Device />` outlet, where react-router's
 * `useOutletContext` has no value to give. The error names this hook, so the
 * misuse points here rather than at a downstream destructure.
 */
export function useDeviceContext(): DeviceContext {
  const context = useOutletContext<DeviceContext | null | undefined>();
  if (!context) {
    throw new Error(
      "useDeviceContext must be called inside a <Device /> outlet",
    );
  }
  return context;
}
